import { useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { toNumber } from "lodash";
import Teachr from "../../../Objects/models/Teachr";
import ClientRepository from "../../../Repositories/ClientRepository";
import { SHOW_ERROR, SHOW_WARNING } from "../../../Redux/Reducers/MessageReducers";
import { TdpDescEnum, TdpEnum } from "../../../Enum/TdpEnum";
import { fullNameChild } from "../../../Utils/Function";
import { vocab } from "../vocabulary";
import { SelectedStudent } from "../types";

/**
 * Sélection d'un élève pour la déclaration : copie des contrôles de
 * `ClientRadioList` (ancien parcours), sans la modale.
 * - élève hors contact Teach'r sans prix horaire ou frais : non sélectionnable ;
 * - statut d'avance immédiate rafraîchi auprès de l'URSSAF à chaque choix ;
 * - compte validé il y a moins de 2 h : déclaration impossible pour l'instant ;
 * - compte bloqué, appareillage en cours ou non finalisé, mandat échu : refus
 *   avec le message de l'énumération TdpDescEnum.
 */
export const isStudentSelectable = (child: any): boolean => {
    const isTeachrContact = child?.isTeachrContact;
    return !(!isTeachrContact && (!child?.pricePerHour || !child?.feesPerHour));
};

/** Date de validation du compte d'avance immédiate (format API « YYYY/MM/DD HH:mm:ss »). */
export const activationMoment = (child: any): moment.Moment | null => {
    if (!child?.activationDate) return null;
    const parsed = moment(child.activationDate, "YYYY/MM/DD HH:mm:ss");
    return parsed.isValid() ? parsed : null;
};

export const useStudentSelection = (teachr: Teachr | undefined) => {
    const dispatch = useDispatch();
    const [checkingId, setCheckingId] = useState<number | null>(null);

    const warn = (message: string) => dispatch({ type: SHOW_WARNING, payload: message });
    const fail = (message: string) => dispatch({ type: SHOW_ERROR, payload: message });

    /** Règle des 2 h après validation du compte, telle que dans ClientRadioList.handleTdpStatusOk. */
    const acceptIfActivatedLongEnough = (child: any): SelectedStudent | null => {
        const activation = activationMoment(child);
        const now = moment();
        if (activation && now.format("DD/MM/YYYY") === activation.format("DD/MM/YYYY") && now.diff(activation, "hours", true) <= 2) {
            const nowWithSeconds = moment().set("second", toNumber(activation.format("ss")));
            const diffMinutes = nowWithSeconds.diff(activation, "minutes");
            const hours = Math.floor(diffMinutes / 60);
            const minutes = diffMinutes % 60;
            const remaining = `${String(1 - hours).padStart(2, "0")}h${String(minutes === 0 && (1 - hours) > 0 ? 0 : 60 - minutes).padStart(2, "0")}`;
            warn(`Vous ne pouvez pas déclarer de ${vocab.course()} pour ${fullNameChild(child?.childFirstName, child?.childLastName)} actuellement, car son compte a été validé à ${activation.format("HH")}h${activation.format("mm")}. Veuillez attendre et réessayer dans ${remaining}.`);
            return null;
        }
        return { childId: child.childId, child, activationDate: activation ? activation.toISOString() : null };
    };

    const blockedMessage = (child: any, error: any): string => {
        const messages: Record<string, string> = {
            [TdpEnum.STATUS_3_NF]: TdpDescEnum.STATUS_3_NF,
            [TdpEnum.STATUS_1_BLOQUE]: TdpDescEnum.STATUS_1_BLOQUE,
            [TdpEnum.STATUS_2_EC]: TdpDescEnum.STATUS_2_EC,
            [TdpEnum.STATUS_5_MANDAT_ECHU]: TdpDescEnum.STATUS_5_MANDAT_ECHU,
        };
        return messages[error?.code] ?? `Le compte de ${fullNameChild(child?.childFirstName, child?.childLastName)} a été bloqué. Vous ne pouvez plus déclarer de ${vocab.course()} pour ${vocab.thisCourse() === "ce cours" ? "cet" : "ce"} ${vocab.client()} pour l'instant.`;
    };

    /**
     * Vérifie le statut de l'élève puis renvoie la sélection, ou null avec un
     * message affiché à l'écran.
     */
    const select = async (child: any): Promise<SelectedStudent | null> => {
        if (!teachr?.id || !child || checkingId !== null) return null;
        if (!isStudentSelectable(child)) {
            warn(TdpDescEnum.STATUS_6_ACTIVE_DEFAULT);
            return null;
        }
        setCheckingId(child.childId);
        try {
            const status: any = await ClientRepository.refreshStatusTdp(child?.clientId, teachr.id);
            if (!status) return null;
            if (status?.etat === "OK") {
                return acceptIfActivatedLongEnough(child);
            }
            fail(blockedMessage(child, status));
            return null;
        } catch (error: any) {
            fail(error?.message === "Invalid Authorization" ? error.message : "Une erreur inconnue, veuillez contacter le service client");
            return null;
        } finally {
            setCheckingId(null);
        }
    };

    return { select, checkingId };
};

export default useStudentSelection;
